import React from "react";
import classnames from "classnames";
import { space, SpaceToken } from "./tokens";
import styles from "./Stack.module.scss";

/**
 * Флекс-контейнер с отступом между детьми из шкалы токенов.
 * Заменяет инлайновые `style={{ display: "flex" }}` и россыпь `marginLeft: "Npx"`
 * у соседних элементов.
 *
 * ```tsx
 * <Stack gap="s" align="center">
 *     <PlanrButton type="secondary">Отмена</PlanrButton>
 *     <PlanrButton type="primary">Сохранить</PlanrButton>
 * </Stack>
 * ```
 */
export const Stack = ({
    direction = "row",
    gap = "s",
    align,
    justify,
    wrap,
    inline,
    grow,
    className,
    style,
    children,
    ...rest
}: StackProps) => (
    <div
        {...rest}
        className={classnames(styles.stack, className, {
            [styles.column]: direction === "column",
            [styles.wrap]: wrap,
            [styles.inline]: inline,
            [styles.grow]: grow,
        })}
        style={{ gap: space[gap], alignItems: align, justifyContent: justify, ...style }}
    >
        {children}
    </div>
);

export interface StackProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "style"> {
    /** Направление: в строку (по умолчанию) или в столбец. */
    direction?: "row" | "column";
    /** Отступ между детьми, по умолчанию `s` (8px). */
    gap?: SpaceToken;
    align?: React.CSSProperties["alignItems"];
    justify?: React.CSSProperties["justifyContent"];
    /** Переносить детей на новую строку, когда не помещаются. */
    wrap?: boolean;
    /** `inline-flex` вместо `flex`. */
    inline?: boolean;
    /** Растянуться на свободное место родителя-флекса. */
    grow?: boolean;
    style?: React.CSSProperties;
    ref?: React.Ref<HTMLDivElement>;
}
